// src/pages/Privacy.tsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Lock,
  Eye,
  Database,
  UserCheck,
  Mail,
  Sparkles,
  Key,
  Globe,
  Cpu,
  FileText,
  Search,
  RefreshCw,
  ShieldOff,
  EyeOff,
  Server,
  ShieldCheck,
  AlertTriangle,
  CheckCircle,
  Users,
  Scale,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/privacy" />

      {/* Header */}
      <section className="relative overflow-hidden pt-24 pb-20 lg:pt-28 lg:pb-24">
        {/* Background Glow Effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-5 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm">
              <ShieldCheck className="h-4 w-4 mr-2" />
              Privacy & Security Commitment
            </div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white mb-8">
              Privacy Policy
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-12">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                <Lock className="h-4 w-4 text-primary" />
                <span className="text-sm text-primary">
                  ISO 27001 Certified
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-lg">
                <Shield className="h-4 w-4 text-emerald-500" />
                <span className="text-sm text-emerald-500">GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 rounded-lg">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-blue-500">Client Trust</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="max-w-5xl mx-auto">
            {/* Table of Contents */}
            <div className="relative mb-16">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
              <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  Table of Contents
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { number: "1", title: "Introduction", icon: FileText },
                    {
                      number: "2",
                      title: "Information Collection",
                      icon: Database,
                    },
                    { number: "3", title: "Data Usage", icon: Eye },
                    { number: "4", title: "Data Sharing", icon: Users },
                    { number: "5", title: "Security Measures", icon: Shield },
                    { number: "6", title: "Your Rights", icon: UserCheck },
                    { number: "7", title: "Contact", icon: Mail },
                    { number: "8", title: "Updates", icon: RefreshCw },
                  ].map((item) => (
                    <a
                      key={item.number}
                      href={`#section-${item.number}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 hover:bg-primary/5 transition-all duration-300 group/item"
                    >
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover/item:scale-110 transition-transform">
                        <span className="font-bold text-primary">
                          {item.number}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white group-hover/item:text-primary transition-colors">
                          {item.title}
                        </h3>
                      </div>
                      <item.icon className="h-4 w-4 text-gray-400 group-hover/item:text-primary transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-16">
              {/* Section 1: Introduction */}
              <div id="section-1" className="relative scroll-mt-24">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <FileText className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <span className="text-sm text-primary font-medium">
                        Section 1
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                        Introduction
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      Welcome to <strong>Pelivor Recovery</strong>. We are
                      committed to protecting your personal information and your
                      right to privacy. If you have any questions or concerns
                      about this privacy policy, or our practices with regards
                      to your personal information, please contact us at{" "}
                      <a
                        href="mailto:privacy@pelivorrecovery.org"
                        className="text-primary hover:underline"
                      >
                        privacy@pelivorrecovery.org
                      </a>
                      .
                    </p>
                    <p>
                      This Privacy Policy describes how we collect, use, store,
                      and share your personal information when you use our
                      recovery services, which include our website, platform,
                      and related services (collectively, the "Services").
                    </p>
                    <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/20">
                      <div className="flex items-start gap-3">
                        <ShieldCheck className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                            Our Commitment
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            We process personal information for recovery
                            services only, never for marketing or third-party
                            sharing without explicit consent.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Information Collection */}
              <div id="section-2" className="relative scroll-mt-24">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                      <Database className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <span className="text-sm text-blue-500 font-medium">
                        Section 2
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                        Information We Collect
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <p className="text-gray-600 dark:text-gray-400">
                      We collect personal information that you voluntarily
                      provide to us when you engage our recovery services,
                      express an interest in our services, or otherwise when you
                      contact us.
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Personal Information Card */}
                      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/20">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <UserCheck className="h-5 w-5 text-primary" />
                          Personal Information
                        </h3>
                        <div className="space-y-3">
                          {[
                            "Account Information: Name, email address, username, password",
                            "Contact Details: Phone number, address for verification",
                            "Verification Documents: For KYC and AML compliance",
                            "Recovery Case Information: Details about lost assets",
                            "Communication Records: Support tickets and messages",
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-start text-sm">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-gray-700 dark:text-gray-300">
                                {item}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Technical Information Card */}
                      <div className="bg-gradient-to-br from-blue-500/5 to-blue-500/10 rounded-xl p-6 border border-blue-500/20">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <Cpu className="h-5 w-5 text-blue-500" />
                          Technical Information
                        </h3>
                        <div className="space-y-3">
                          {[
                            "Device Information: IP address, browser type, device details",
                            "Usage Data: Platform interactions and feature usage",
                            "Security Logs: Authentication attempts and access logs",
                            "Blockchain Data: Public wallet addresses for recovery",
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-start text-sm">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-gray-700 dark:text-gray-300">
                                {item}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: How We Use Information */}
              <div id="section-3" className="relative scroll-mt-24">
                <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                      <Eye className="h-6 w-6 text-emerald-500" />
                    </div>
                    <div>
                      <span className="text-sm text-emerald-500 font-medium">
                        Section 3
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                        How We Use Your Information
                      </h2>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      {
                        title: "Service Delivery",
                        description:
                          "Providing recovery services, processing requests, and managing cases",
                        icon: Server,
                        color: "from-primary/10 to-primary/5",
                        border: "border-primary/20",
                      },
                      {
                        title: "Security Protection",
                        description:
                          "Monitoring for fraudulent activity and verifying identities",
                        icon: Shield,
                        color: "from-emerald-500/10 to-emerald-500/5",
                        border: "border-emerald-500/20",
                      },
                      {
                        title: "Legal Compliance",
                        description:
                          "Meeting regulatory requirements and law enforcement obligations",
                        icon: Scale,
                        color: "from-blue-500/10 to-blue-500/5",
                        border: "border-blue-500/20",
                      },
                      {
                        title: "Client Support",
                        description:
                          "Responding to inquiries and providing recovery updates",
                        icon: Users,
                        color: "from-purple-500/10 to-purple-500/5",
                        border: "border-purple-500/20",
                      },
                      {
                        title: "Case Management",
                        description:
                          "Tracking recovery progress and maintaining case documentation",
                        icon: FileText,
                        color: "from-yellow-500/10 to-yellow-500/5",
                        border: "border-yellow-500/20",
                      },
                      {
                        title: "Service Improvement",
                        description:
                          "Enhancing our recovery processes and security measures",
                        icon: Sparkles,
                        color: "from-rose-500/10 to-rose-500/5",
                        border: "border-rose-500/20",
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className={`bg-gradient-to-br ${item.color} rounded-xl p-5 border ${item.border} hover:scale-[1.02] transition-all duration-300`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <item.icon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                          <h4 className="font-bold text-gray-900 dark:text-white">
                            {item.title}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Section 4: Data Sharing */}
              <div id="section-4" className="relative scroll-mt-24">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                      <Users className="h-6 w-6 text-purple-500" />
                    </div>
                    <div>
                      <span className="text-sm text-purple-500 font-medium">
                        Section 4
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                        Data Sharing & Disclosure
                      </h2>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      We do not sell, rent, or trade your personal information.
                      Data is shared only when necessary for service delivery or
                      legal compliance:
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 rounded-xl p-5 border border-green-500/20">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          Authorized Sharing
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                            Trusted legal advisors for recovery cases
                          </li>
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                            Technical service providers for security
                          </li>
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                            Banking partners for asset transfers
                          </li>
                        </ul>
                      </div>

                      <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 rounded-xl p-5 border border-red-500/20">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                          Legal Requirements
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                            Court orders and legal proceedings
                          </li>
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                            Regulatory compliance requests
                          </li>
                          <li className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                            Fraud prevention and investigation
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 5: Data Security */}
              <div id="section-5" className="relative scroll-mt-24">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <span className="text-sm text-primary font-medium">
                        Section 5
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                        Data Security
                      </h2>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <p className="text-gray-600 dark:text-gray-400">
                      We implement enterprise-grade security measures to protect
                      your information:
                    </p>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[
                        {
                          title: "AES-256 Encryption",
                          icon: Lock,
                          color: "from-primary/10 to-primary/5",
                        },
                        {
                          title: "Multi-Factor Auth",
                          icon: Key,
                          color: "from-blue-500/10 to-blue-500/5",
                        },
                        {
                          title: "Regular Audits",
                          icon: Search,
                          color: "from-emerald-500/10 to-emerald-500/5",
                        },
                        {
                          title: "Secure Storage",
                          icon: Database,
                          color: "from-purple-500/10 to-purple-500/5",
                        },
                        {
                          title: "Access Controls",
                          icon: EyeOff,
                          color: "from-rose-500/10 to-rose-500/5",
                        },
                        {
                          title: "Incident Response",
                          icon: ShieldOff,
                          color: "from-yellow-500/10 to-yellow-500/5",
                        },
                      ].map((item, idx) => (
                        <div
                          key={idx}
                          className={`bg-gradient-to-br ${item.color} rounded-xl p-4 border border-gray-200/30 dark:border-gray-700/30 flex flex-col items-center text-center`}
                        >
                          <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center mb-3">
                            <item.icon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                          </div>
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                            {item.title}
                          </h4>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 6: Your Rights */}
              <div id="section-6" className="relative scroll-mt-24">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-500/10 via-green-500/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                      <UserCheck className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <span className="text-sm text-green-500 font-medium">
                        Section 6
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                        Your Privacy Rights
                      </h2>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      {
                        title: "Access & Correction",
                        description:
                          "View and update your information through account settings",
                        color: "from-green-500/10 to-green-500/5",
                      },
                      {
                        title: "Data Portability",
                        description:
                          "Request your data in a structured, machine-readable format",
                        color: "from-blue-500/10 to-blue-500/5",
                      },
                      {
                        title: "Deletion Rights",
                        description:
                          "Request removal of your data, subject to legal requirements",
                        color: "from-red-500/10 to-red-500/5",
                      },
                      {
                        title: "Opt-Out",
                        description:
                          "Withdraw consent for specific processing activities",
                        color: "from-purple-500/10 to-purple-500/5",
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className={`bg-gradient-to-br ${item.color} rounded-xl p-5 border border-gray-200/30 dark:border-gray-700/30 hover:scale-[1.02] transition-all duration-300`}
                      >
                        <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200/30 dark:border-gray-700/30">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      To exercise any of these rights, please contact our Data
                      Protection Officer at{" "}
                      <a
                        href="mailto:privacy@pelivorrecovery.org"
                        className="text-primary hover:underline"
                      >
                        privacy@pelivorrecovery.org
                      </a>
                      . We will respond within 30 days.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 7: Contact */}
              <div id="section-7" className="relative scroll-mt-24">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                      <Mail className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <span className="text-sm text-blue-500 font-medium">
                        Section 7
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                        Contact Us
                      </h2>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-8 border border-primary/20">
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white mb-4">
                          Contact Information
                        </h4>
                        <div className="space-y-3">
                          <p className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <Mail className="h-4 w-4 text-primary" />
                            <a
                              href="mailto:privacy@pelivorrecovery.org"
                              className="hover:text-primary transition-colors"
                            >
                              privacy@pelivorrecovery.org
                            </a>
                          </p>
                          <p className="text-gray-600 dark:text-gray-400">
                            <strong>Data Protection Officer:</strong>
                            <br />
                            Pelivor Recovery Privacy Team
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                            Response time: 30 days maximum
                          </p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white mb-4">
                          Compliance Information
                        </h4>
                        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                          <p>• Registered with ICO (UK)</p>
                          <p>• GDPR Compliant</p>
                          <p>• ISO 27001 Certified</p>
                          <p>• Regular third-party audits</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 8: Updates */}
              <div id="section-8" className="relative scroll-mt-24">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-transparent rounded-3xl blur-xl opacity-30"></div>
                <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                      <RefreshCw className="h-6 w-6 text-purple-500" />
                    </div>
                    <div>
                      <span className="text-sm text-purple-500 font-medium">
                        Section 8
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                        Policy Updates
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      We may update this Privacy Policy from time to time. The
                      updated version will be indicated by an updated "Last
                      updated" date and the updated version will be effective as
                      soon as it is accessible.
                    </p>
                    <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                            Notification of Changes
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            We will notify you of any material changes via email
                            or platform notifications at least 30 days before
                            the changes take effect.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions Footer */}
      <div className="bg-gray-900 py-8">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <img src={logo} alt="Pelivor Recovery" className="h-6 w-auto" />
              <span className="text-white font-medium">Pelivor Recovery</span>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/terms"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Terms of Service
              </Link>
              <span className="text-gray-400">•</span>
              <a
                href="mailto:privacy@pelivorrecovery.org"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Privacy Questions
              </a>
              <span className="text-gray-400">•</span>
              <Link
                to="/dashboard"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Client Portal
              </Link>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 mt-8 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Pelivor Recovery.
              pelivorrecovery.org. All rights reserved.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Professional cryptocurrency recovery services. SOC 2 Type II & ISO
              27001 Certified.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
